/** Strings of the agent tabs (`components/agents/tabs/*`): `agentTabsEn` is the source, `agentTabsCs` must have the same keys. */
export const agentTabsEn = {
  agentTabs: {
    learningOutcome: {
      autoOnly: "Only auto-decided",
      decision: {
        auto_accepted: "Radim activated it",
        auto_dismissed: "Radim dismissed it",
        needs_person: "Radim leaves it to you",
      },
      reasons: "Addresses",
      effect: "Effect: {{before}} → {{after}}",
      effectHint: "occurrences of its reason 4 weeks before activation and after it",
      effectWeeks: "after {{count}} weeks",
      retire: "Retire",
      keep: "Keep active",
    },
    common: { loading: "Loading…" },
    skills: {
      intro:
        "Company skills from the netlion.cz/claude-skills marketplace, as the agent's checkout has them. Only the ones switched on get into its runs; a new skill starts off.",
      loading: "Loading…",
      loadFailed: "Couldn't load the skills.",
      empty:
        "No skills yet: the agent reports them once it syncs the marketplace (every 10 minutes while it runs).",
      count: "{{on}} of {{total}} on",
      search: "Search skills",
      noMatch: "No skill matches.",
      details: "Details of {{name}}",
      toggle: "Allow {{name}}",
      env: "Env vars",
      envHelp:
        "Given to the agent's runs while the skill is on. Values are stored sealed and never shown again.",
      envNone: "No env vars.",
      keyPlaceholder: "NAME",
      valuePlaceholder: "Value",
      add: "Add",
      remove: "Remove {{key}}",
      saved: "Saved.",
      saveFailed: "Couldn't save the skill.",
      changedBy: "Changed {{at}} by {{who}}",
      changedAt: "Changed {{at}}",
    },
    dismiss: {
      placeholder: "Why (optional)",
      ariaLabel: "Why it is dismissed",
      button: "Dismiss",
    },
    email: {
      title: "Email",
      intro:
        "Forward client emails to the watched mailbox; the AI proposes follow-ups into the Suggestions queue.",
      tabs: { routing: "Routing", mailbox: "Mailbox", history: "History" },
      historyEmpty: "No analysed emails yet. Forward a client email to the watched mailbox.",
    },
    meetings: {
      title: "Meetings",
      intro: "Every analysed meeting transcript. Open one to see its summary and suggestions.",
      add: "Add meeting",
      dialogTitle: "Analyse a meeting transcript",
      added: "Added to the Suggestions queue",
      historyEmpty: "No meetings analysed yet. Use “Add meeting” to analyse a transcript.",
    },
    discord: {
      title: "Discord",
      intro:
        "Watched channels are read continuously; open one to see its transcript and what it led to.",
      pickerError: "Couldn't list the bot's channels.",
      watchNew: "Watch new channel",
      watchTitle: "Watch a channel",
      watchHint: "Text channels the bot can see. Watching starts reading the channel continuously.",
      nothingToWatch: "No more channels to watch.",
      watch: "Watch",
      notConnected: "The Discord bot isn't connected. Open <b>Bot</b> (top right) for setup.",
      watchedEmpty: "No watched channels yet. Use <b>Watch new channel</b> to start reading one.",
      processed: "{{done}}/{{total}} processed",
      projectFor: "Project for {{name}}",
      noProject: "— no project —",
      unwatch: "Unwatch",
      bot: "Bot",
      botTitle: "Discord bot",
      off: "The Discord bot is not enabled on the server. Set <c>DISCORD_BOT_TOKEN</c>.",
      step1: "Create an application + bot at <c>discord.com/developers</c> and copy its token.",
      step2: "On the Bot page enable the <b>Message Content</b> intent.",
      step3:
        "Invite the bot to your server — View Channels + Read Message History + Connect (permissions integer <c>1115136</c>).",
      connectedAs: "Connected as <b>{{name}}</b>",
      noGuilds:
        "The bot isn't in any server yet — invite it from the Discord developer portal (OAuth2 → URL generator, scope <c>bot</c>).",
      servers: "Servers: {{list}}",
      tokenBroken: "The bot token doesn't work right now.",
    },
    triageLearnings: {
      title: "What Argus has learned",
      intro:
        "Signatures whose tickets you deleted or closed without anyone working on them. A rule does nothing until you activate it — and dismissing one takes effect immediately: the next sweep opens that error again.",
      loadFailed: "Couldn't load the rules.",
      mining: "Mining is running — switch tabs in a moment to reload the list.",
      miningFailed: "Couldn't start the mining run.",
      starting: "Starting…",
      mineNow: "Go through tickets now",
      tabs: {
        draft: "Proposed",
        active: "Active",
        expired: "Expired",
        dismissed: "Dismissed",
        all: "All",
      },
      emptyDraft: "Nothing waiting — Argus hasn't found a signature people throw away repeatedly.",
      empty: "Nothing here.",
      allProjects: "All projects",
      thrownAway: "thrown away {{count}}×",
      applied: "applied {{count}}×",
      action: { drop: "don't open a ticket", low: "open at low priority" },
      activatedBy: "activated by {{name}}",
      dismissedBy: "dismissed by {{name}}",
      someone: "someone",
      saveDecisionFailed: "Couldn't save the decision.",
      saveChangeFailed: "Couldn't save the change.",
      activate: "Activate",
      dismiss: "Dismiss",
      lowInstead: "Low priority instead",
      dropInstead: "Don't open at all instead",
    },
    logSources: {
      title: "Log sources",
      intro:
        "Grafana/Loki servers the log-triage sweep polls for production errors, and which container maps to which project. No AI in this path — see the sweep's own ticket template.",
      createFailed: "Couldn't create the source — check the slug is unique and every field is set.",
      failed: "Couldn't {{what}}",
      error: "Error",
      what: {
        discover: "discover sources",
        delete: "delete the source",
        addMap: "add the mapping",
        removeMap: "remove the mapping",
      },
      discoverOpen: "Discover from Grafana",
      discoverHint:
        "Creates one source per <c>host</c> label and maps every production compose service (<c>&lt;project&gt;-main-&lt;service&gt;</c>) to the project whose slug matches. Existing sources and mappings are kept; unmatched services are listed for you to map by hand.",
      ph: {
        grafanaUrl: "Grafana URL (https://grafana.example.com)",
        tokenEnvDiscover: "Env var holding the Bearer token (e.g. GRAFANA_TOKEN_PROD)",
        lokiUidShort: "Loki datasource uid (optional)",
        name: "Name (e.g. Prod server 1)",
        slug: "Slug (e.g. prod-1)",
        selector: 'LogQL selector (e.g. {compose_project="hub"})',
        errorFilter: "Error filter override (optional, raw LogQL)",
        lokiUid: "Loki datasource uid (optional — auto-resolved if left blank)",
        tokenEnv: "Env var holding the Bearer token (e.g. GRAFANA_TOKEN_PROD1)",
        container: "Container name",
      },
      discovering: "Discovering…",
      discoverRun: "Discover & map",
      hostSummary: "{{host}} — {{mapped}} mapped, {{unmatched}} unmatched, {{skipped}} skipped",
      hostError: "{{host}} — query failed: {{error}}",
      newSource: "New source",
      newTitle: "New log source",
      noFallbackLong: "No fallback project (skip unmapped containers)",
      create: "Create",
      empty: "No log sources configured yet.",
      enabled: "Enabled",
      fallbackFor: "Fallback project for {{name}}",
      noFallback: "No fallback (skip unmapped)",
      delete: "Delete",
      checkedUpTo: "Checked up to {{date}}",
      notPolled: "Not polled yet",
      containerToProject: "Container → project",
      noMaps: "No containers mapped — errors fall to the fallback project above (or are skipped).",
      remove: "Remove",
      selectProject: "Select project…",
      add: "Add",
    },
    metodejLearnings: {
      addFailed: "Couldn't add the lesson.",
      addTitle: "Add a lesson",
      addHint:
        "Something Metoděj should know that nobody said in a prompt. It is active at once, and Vratislav judges plans by it too.",
      newLesson: "New lesson",
      appliesTo: "Lesson applies to",
      generalLong: "General style (every project)",
      general: "General style",
      add: "Add",
      loadFailed: "Couldn't load learnings.",
      saveFailed: "Couldn't save that.",
      mineStarted: "Mining started, check back in a few minutes.",
      aiNotConfigured: "AI isn't configured.",
      mineFailed: "Couldn't start mining.",
      title: "Metoděj's Learnings",
      intro:
        "Lessons Hubert found in people's own prompts to Claude Code: conventions of a project, and how someone wants work done in general. Only Active ones reach Metoděj, and taking one back removes it from his next ticket.",
      mineNow: "Mine now",
      tabs: {
        draft: "Draft",
        active: "Active",
        retire_proposed: "To retire",
        expired: "Expired",
        dismissed: "Dismissed",
        all: "All",
      },
      empty: "Nothing here.",
      someone: "someone",
      writtenBy: "written by {{name}}",
      seen: "seen {{count}}× · from {{name}}'s prompts",
      activatedBy: "activated by {{name}}",
      dismissedBy: "dismissed by {{name}}",
      why: "Why: ",
      dismissedByHub: "Dismissed by Hub: ",
      text: "Learning text",
      whySummary: "Why",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      activate: "Activate",
      backToDraft: "Back to draft",
    },
    ruleLearnings: {
      addFailed: "Couldn't add the rule.",
      addTitle: "Add a rule",
      newRule: "New rule",
      appliesTo: "Rule applies to",
      generalLong: "Every project",
      general: "Every project",
      add: "Add",
      loadFailed: "Couldn't load learnings.",
      saveFailed: "Couldn't save that.",
      tabs: {
        draft: "Draft",
        active: "Active",
        retire_proposed: "To retire",
        expired: "Expired",
        dismissed: "Dismissed",
        all: "All",
      },
      empty: "Nothing here.",
      writtenByHand: "written by hand",
      seen: "seen {{count}}×",
      activatedBy: "activated by {{name}}",
      dismissedBy: "dismissed by {{name}}",
      why: "Why: ",
      dismissedByHub: "Dismissed by Hub: ",
      text: "Rule text",
      whySummary: "Why",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      activate: "Activate",
      backToDraft: "Back to draft",
      agents: {
        vratislav: {
          title: "Vratislav's Learnings",
          intro:
            "Rules from where Vratislav's verdicts were overruled: Radim as arbiter kept the other side's plan or criteria, he withdrew a review return the people disputed, or he merged with no new work after escalating. A reason seen 3× in a week on a project becomes a proposal. Only Active rules reach his reviews of merge requests, plans and criteria.",
          addHint: "Something Vratislav should keep in mind when he reviews. It is active at once.",
        },
        prokop: {
          title: "Prokop's Rules",
          intro:
            "Rules for how Prokop tests, from where his results stayed unclear, the ticket was in the wrong state, he called it untestable, or a failed test was disputed and the ticket then passed. A reason seen 3× in a week on a project becomes a proposal. Only Active rules reach his tests and the criteria he writes. Missing capabilities stay in Learnings.",
          addHint: "Something Prokop should keep in mind when he tests. It is active at once.",
        },
      },
    },
    reviewerLearnings: {
      loadFailed: "Couldn't load learnings.",
      saveFailed: "Couldn't save that.",
      sendFailed: "Couldn't send it.",
      lookBackStarted: "Looking back at finished reviews, check back in a few minutes.",
      aiNotConfigured: "AI isn't configured.",
      lookBackFailed: "Couldn't start the look back.",
      title: "Radim's Learnings",
      intro:
        "After every finished review, Hubert reads the meeting back and proposes what Radim should do differently. A rule reaches Radim only once it's Active, from his next meeting on (spoken ones too), and taking it back removes it again. A bug is something in Hub's own tools: send it to Metoděj as a Hub ticket.",
      lookBackNow: "Look back now",
      tabs: {
        draft: "Draft",
        active: "Active",
        retire_proposed: "To retire",
        expired: "Expired",
        dismissed: "Dismissed",
        all: "All",
      },
      empty: "Nothing here.",
      kind: { bug: "Bug", asr: "Word", rule: "Rule" },
      everyProject: "Every project",
      seen: "seen {{count}}×",
      fromReview: "from the {{mode}} review of {{project}}, {{date}}",
      fromReviewAll: "from a {{mode}} review across all projects, {{date}}",
      sentAs: "sent as #{{id}}",
      activatedBy: "activated by {{name}}",
      dismissedBy: "dismissed by {{name}}",
      why: "Why: ",
      dismissedByHub: "Dismissed by Hub: ",
      text: "Learning text",
      whySummary: "Why",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      sendToMetodej: "Send to Metoděj",
      activate: "Activate",
      backToDraft: "Back to draft",
    },
    testerLearnings: {
      loadFailed: "Couldn't load learnings.",
      title: "What Prokop can't test yet",
      intro:
        "Each card is a capability Prokop lacked when a ticket stayed unverified, with the tickets it blocked. Send it to Metoděj and it becomes a feature to build; dismiss it and Prokop stops proposing it.",
      tabs: { draft: "Proposed", dismissed: "Dismissed" },
      empty: "Nothing here.",
      blocked_one: "blocked {{count}} ticket",
      blocked_few: "blocked {{count}} tickets",
      blocked_other: "blocked {{count}} tickets",
      sentBadge: "sent to Metoděj",
      tickets: "Tickets",
      why: "Why: ",
      dismissedByHub: "Dismissed by Hub: ",
      feature: "Feature",
      projectFor: "Project for the feature",
      pickProject: "Pick a project",
      sendFailed: "Couldn't send that to Metoděj.",
      saveFailed: "Couldn't save that.",
      send: "Send to Metoděj",
      backToProposed: "Back to proposed",
    },
    wikiKeeper: {
      ticketFor: "Ticket for Metoděj:",
      companyWiki: "Company wiki:",
      skipped: "Skipped: {{title}} ({{reason}})",
      seedMissing: "No seed found in the repo.",
      seed: "Seed: {{checked}} accounts, {{present}} already in the Vault",
      seedFiled: ", added: {{list}}",
      seedNoPassword: ", without a readable password: {{list}}",
      loadFailed: "Couldn't load the runs.",
      auditStarted: "Audit started.",
      watchStarted: "Change check started.",
      startFailed: "Couldn't start it.",
      checkNow: "Check changes now",
      runAudit: "Run wiki audit",
      loading: "Loading…",
      none: "Kosmas hasn't run yet.",
      typeAudit: "Audit",
      typeChanges: "Changes",
      quiet_one: "{{count}} project without changes",
      quiet_few: "{{count}} projects without changes",
      quiet_other: "{{count}} projects without changes",
      wikiOk: "The wiki is in sync.",
    },
    whatsapp: {
      pairFailed: "Couldn't start WhatsApp pairing.",
      unlinkFailed: "Couldn't unlink WhatsApp.",
      historyAsking: "Asking the phone for history…",
      historyAskingDesc_one:
        "{{count}} paired chat. The result appears above the list — it can come back empty.",
      historyAskingDesc_few:
        "{{count}} paired chats. The result appears above the list — it can come back empty.",
      historyAskingDesc_other:
        "{{count}} paired chats. The result appears above the list — it can come back empty.",
      historyFailed: "Couldn't start the history load.",
      dateChanged: "Date changed.",
      dateChangedDesc: "Use Load full history (or re-link) to pull what's missing.",
      dateFailed: "Couldn't change the date.",
      intro:
        "Watched chats are read continuously; open one to see its transcript and what it led to.",
      pairNew: "Pair new chat",
      pairTitle: "Pair a chat",
      pairHint:
        "Groups the linked account is in, and private chats it has a message in (most recently active first). Pairing starts watching the chat. A chat with no project is routed per message: Hubert picks the projects it is about.",
      nothingToPair: "No more recent chats to pair.",
      pair: "Pair",
      loadHistory: "Load full history",
      pulling: "Pulling history from the phone for {{count}} chat(s)…",
      pulled: "Last history pull brought {{count}} message(s).",
      pulledNothing:
        "Last history pull brought nothing — the phone answered with no older messages. The dependable way to fill a chat is <b>Account → Unlink</b> and link again: on pairing the phone pushes its whole history.",
      notConnected: "WhatsApp isn't connected. Use <b>Account</b> (top right) to link it.",
      pairedEmpty: "No paired chats yet. Use <b>Pair new chat</b> to start watching a chat.",
      processed: "{{done}}/{{total}} processed",
      projectFor: "Project for {{name}}",
      hubertPicks: "— Hubert picks —",
      unpair: "Unpair",
      change: "change",
      suggestionsFrom: "Suggestions from — {{name}}",
      relabelHint:
        "Only re-labels the chat: stored messages stay, whatever you pick. Nothing is unpaired.",
      save: "Save",
      account: "Account",
      accountTitle: "WhatsApp account",
      loading: "Loading…",
      off: "WhatsApp is not enabled on the server. Set <c>WHATSAPP_ENABLED=true</c>.",
      linkedAs: "Linked as <b>{{name}}</b>",
      relinkHint:
        "Re-linking makes the phone push its whole history again — the way to fill chats that were paired while the account was already linked.",
      unlink: "Unlink",
      scan: "Open WhatsApp → <b>Settings → Linked devices → Link a device</b> and scan:",
      qrAlt: "WhatsApp pairing QR code",
      connecting: "Connecting to WhatsApp…",
      linkPrompt: "Link a WhatsApp account to watch chats.",
      waitingScan: "Waiting for scan…",
      starting: "Starting…",
      link: "Link WhatsApp",
    },
  },
};
export const agentTabsCs: typeof agentTabsEn = {
  agentTabs: {
    learningOutcome: {
      autoOnly: "Jen rozhodnuté samy",
      decision: {
        auto_accepted: "Radim ho zapnul",
        auto_dismissed: "Radim ho zamítl",
        needs_person: "Radim ho nechává na vás",
      },
      reasons: "Řeší",
      effect: "Efekt: {{before}} → {{after}}",
      effectHint: "kolikrát se jeho důvod objevil 4 týdny před zapnutím a po něm",
      effectWeeks: "po {{count}} týdnech",
      retire: "Zrušit",
      keep: "Nechat aktivní",
    },
    common: { loading: "Načítání…" },
    skills: {
      intro:
        "Firemní skilly z marketplace netlion.cz/claude-skills, jak je má agent ve svém checkoutu. Do jeho běhů jdou jen zapnuté, nový skill začíná vypnutý.",
      loading: "Načítám…",
      loadFailed: "Skilly se nepodařilo načíst.",
      empty:
        "Zatím žádné skilly: agent je nahlásí, až si stáhne marketplace (při běhu každých 10 minut).",
      count: "Zapnuto {{on}} z {{total}}",
      search: "Hledat skill",
      noMatch: "Žádný skill neodpovídá.",
      details: "Podrobnosti {{name}}",
      toggle: "Povolit {{name}}",
      env: "Proměnné",
      envHelp:
        "Dostanou je běhy agenta, dokud je skill zapnutý. Hodnoty se ukládají zapečetěné a už se nezobrazí.",
      envNone: "Žádné proměnné.",
      keyPlaceholder: "NAZEV",
      valuePlaceholder: "Hodnota",
      add: "Přidat",
      remove: "Odebrat {{key}}",
      saved: "Uloženo.",
      saveFailed: "Skill se nepodařilo uložit.",
      changedBy: "Změněno {{at}}, {{who}}",
      changedAt: "Změněno {{at}}",
    },
    dismiss: {
      placeholder: "Proč (nepovinné)",
      ariaLabel: "Proč se zamítá",
      button: "Zamítnout",
    },
    email: {
      title: "E-mail",
      intro:
        "Přeposílejte e-maily klientů do sledované schránky; AI navrhne navazující kroky do fronty návrhů.",
      tabs: { routing: "Směrování", mailbox: "Schránka", history: "Historie" },
      historyEmpty:
        "Zatím žádné analyzované e-maily. Přepošlete e-mail klienta do sledované schránky.",
    },
    meetings: {
      title: "Schůzky",
      intro: "Všechny analyzované přepisy schůzek. Otevřením uvidíte shrnutí a návrhy.",
      add: "Přidat schůzku",
      dialogTitle: "Analyzovat přepis schůzky",
      added: "Přidáno do fronty návrhů",
      historyEmpty:
        "Zatím žádné analyzované schůzky. Přepis analyzujete tlačítkem „Přidat schůzku“.",
    },
    discord: {
      title: "Discord",
      intro:
        "Sledované kanály se čtou průběžně; otevřením uvidíte jejich přepis a co z něj vzniklo.",
      pickerError: "Kanály bota se nepodařilo vypsat.",
      watchNew: "Sledovat nový kanál",
      watchTitle: "Sledovat kanál",
      watchHint: "Textové kanály, které bot vidí. Sledování začne kanál průběžně číst.",
      nothingToWatch: "Žádné další kanály ke sledování.",
      watch: "Sledovat",
      notConnected:
        "Discord bot není připojený. Návod k nastavení najdete pod <b>Bot</b> (vpravo nahoře).",
      watchedEmpty:
        "Zatím žádné sledované kanály. Čtení zahájíte tlačítkem <b>Sledovat nový kanál</b>.",
      processed: "zpracováno {{done}}/{{total}}",
      projectFor: "Projekt pro {{name}}",
      noProject: "— bez projektu —",
      unwatch: "Přestat sledovat",
      bot: "Bot",
      botTitle: "Discord bot",
      off: "Discord bot není na serveru zapnutý. Nastavte <c>DISCORD_BOT_TOKEN</c>.",
      step1: "Vytvořte aplikaci + bota na <c>discord.com/developers</c> a zkopírujte jeho token.",
      step2: "Na stránce Bot zapněte oprávnění <b>Message Content</b>.",
      step3:
        "Pozvěte bota na svůj server — View Channels + Read Message History + Connect (číslo oprávnění <c>1115136</c>).",
      connectedAs: "Připojen jako <b>{{name}}</b>",
      noGuilds:
        "Bot zatím není na žádném serveru — pozvěte ho z Discord developer portálu (OAuth2 → URL generator, scope <c>bot</c>).",
      servers: "Servery: {{list}}",
      tokenBroken: "Token bota teď nefunguje.",
    },
    triageLearnings: {
      title: "Co se Argus naučil",
      intro:
        "Signatury, jejichž tikety jste smazali nebo zavřeli, aniž na nich někdo pracoval. Pravidlo nic nedělá, dokud ho neaktivujete — a zamítnutí platí hned: další průchod tu chybu otevře znovu.",
      loadFailed: "Pravidla se nepodařilo načíst.",
      mining: "Probíhá vyhodnocování — za chvíli přepněte záložku a seznam se obnoví.",
      miningFailed: "Vyhodnocování se nepodařilo spustit.",
      starting: "Spouštím…",
      mineNow: "Projít tikety teď",
      tabs: {
        draft: "Navržená",
        active: "Aktivní",
        expired: "Vypršelá",
        dismissed: "Zamítnutá",
        all: "Všechna",
      },
      emptyDraft: "Nic nečeká — Argus nenašel signaturu, kterou lidé opakovaně zahazují.",
      empty: "Nic tu není.",
      allProjects: "Všechny projekty",
      thrownAway: "zahozeno {{count}}×",
      applied: "použito {{count}}×",
      action: { drop: "tiket neotevírat", low: "otevřít s nízkou prioritou" },
      activatedBy: "aktivoval(a) {{name}}",
      dismissedBy: "zamítl(a) {{name}}",
      someone: "někdo",
      saveDecisionFailed: "Rozhodnutí se nepodařilo uložit.",
      saveChangeFailed: "Změnu se nepodařilo uložit.",
      activate: "Aktivovat",
      dismiss: "Zamítnout",
      lowInstead: "Raději nízká priorita",
      dropInstead: "Raději vůbec neotevírat",
    },
    logSources: {
      title: "Zdroje logů",
      intro:
        "Servery Grafana/Loki, které průchod log-triage dotazuje na chyby z produkce, a který kontejner patří ke kterému projektu. V této cestě není žádná AI — viz vlastní šablona tiketu průchodu.",
      createFailed:
        "Zdroj se nepodařilo vytvořit — zkontrolujte, že slug je unikátní a všechna pole jsou vyplněná.",
      failed: "Nepodařilo se {{what}}",
      error: "Chyba",
      what: {
        discover: "zjistit zdroje",
        delete: "smazat zdroj",
        addMap: "přidat mapování",
        removeMap: "odebrat mapování",
      },
      discoverOpen: "Zjistit z Grafany",
      discoverHint:
        "Vytvoří jeden zdroj na každý label <c>host</c> a každou produkční službu compose (<c>&lt;projekt&gt;-main-&lt;služba&gt;</c>) namapuje na projekt, jehož slug odpovídá. Stávající zdroje a mapování zůstanou; nenamapované služby se vypíšou k ručnímu namapování.",
      ph: {
        grafanaUrl: "URL Grafany (https://grafana.example.com)",
        tokenEnvDiscover: "Proměnná prostředí s Bearer tokenem (např. GRAFANA_TOKEN_PROD)",
        lokiUidShort: "UID datasource Loki (nepovinné)",
        name: "Název (např. Prod server 1)",
        slug: "Slug (např. prod-1)",
        selector: 'LogQL selektor (např. {compose_project="hub"})',
        errorFilter: "Vlastní filtr chyb (nepovinné, čisté LogQL)",
        lokiUid: "UID datasource Loki (nepovinné — když zůstane prázdné, zjistí se samo)",
        tokenEnv: "Proměnná prostředí s Bearer tokenem (např. GRAFANA_TOKEN_PROD1)",
        container: "Název kontejneru",
      },
      discovering: "Zjišťuji…",
      discoverRun: "Zjistit a namapovat",
      hostSummary:
        "{{host}} — namapováno {{mapped}}, nenamapováno {{unmatched}}, přeskočeno {{skipped}}",
      hostError: "{{host}} — dotaz selhal: {{error}}",
      newSource: "Nový zdroj",
      newTitle: "Nový zdroj logů",
      noFallbackLong: "Bez záložního projektu (nenamapované kontejnery se přeskočí)",
      create: "Vytvořit",
      empty: "Zatím nejsou nastavené žádné zdroje logů.",
      enabled: "Zapnuto",
      fallbackFor: "Záložní projekt pro {{name}}",
      noFallback: "Bez záložního (nenamapované přeskočit)",
      delete: "Smazat",
      checkedUpTo: "Zkontrolováno do {{date}}",
      notPolled: "Zatím nedotazováno",
      containerToProject: "Kontejner → projekt",
      noMaps:
        "Žádné kontejnery nejsou namapované — chyby jdou na záložní projekt výše (nebo se přeskočí).",
      remove: "Odebrat",
      selectProject: "Vyberte projekt…",
      add: "Přidat",
    },
    metodejLearnings: {
      addFailed: "Poučení se nepodařilo přidat.",
      addTitle: "Přidat poučení",
      addHint:
        "Něco, co by Metoděj měl vědět, ale nikdo to nenapsal do promptu. Je hned aktivní a Vratislav podle něj posuzuje i plány.",
      newLesson: "Nové poučení",
      appliesTo: "Poučení platí pro",
      generalLong: "Obecný styl (všechny projekty)",
      general: "Obecný styl",
      add: "Přidat",
      loadFailed: "Poučení se nepodařilo načíst.",
      saveFailed: "Uložení se nepodařilo.",
      mineStarted: "Vyhodnocování začalo, zkuste to za pár minut.",
      aiNotConfigured: "AI není nastavená.",
      mineFailed: "Vyhodnocování se nepodařilo spustit.",
      title: "Poučení Metoděje",
      intro:
        "Poučení, která Hubert našel v promptech lidí pro Claude Code: konvence projektu a obecně to, jak kdo chce mít práci udělanou. Metoděj dostane jen aktivní a po jejich vzetí zpět zmizí z jeho dalšího tiketu.",
      mineNow: "Vyhodnotit teď",
      tabs: {
        draft: "Návrhy",
        active: "Aktivní",
        retire_proposed: "Ke zrušení",
        expired: "Vypršelá",
        dismissed: "Zamítnutá",
        all: "Všechna",
      },
      empty: "Nic tu není.",
      someone: "někdo",
      writtenBy: "zapsal(a) {{name}}",
      seen: "viděno {{count}}× · z promptů uživatele {{name}}",
      activatedBy: "aktivoval(a) {{name}}",
      dismissedBy: "zamítl(a) {{name}}",
      why: "Proč: ",
      dismissedByHub: "Zamítnuto Hubem: ",
      text: "Text poučení",
      whySummary: "Proč",
      save: "Uložit",
      cancel: "Zrušit",
      edit: "Upravit",
      activate: "Aktivovat",
      backToDraft: "Zpět do návrhů",
    },
    ruleLearnings: {
      addFailed: "Pravidlo se nepodařilo přidat.",
      addTitle: "Přidat pravidlo",
      newRule: "Nové pravidlo",
      appliesTo: "Pravidlo platí pro",
      generalLong: "Všechny projekty",
      general: "Všechny projekty",
      add: "Přidat",
      loadFailed: "Learnings se nepodařilo načíst.",
      saveFailed: "Nepodařilo se to uložit.",
      tabs: {
        draft: "Návrhy",
        active: "Aktivní",
        retire_proposed: "Ke zrušení",
        expired: "Vypršelá",
        dismissed: "Zamítnuté",
        all: "Vše",
      },
      empty: "Nic tu není.",
      writtenByHand: "zapsáno ručně",
      seen: "viděno {{count}}×",
      activatedBy: "aktivoval(a) {{name}}",
      dismissedBy: "zamítl(a) {{name}}",
      why: "Proč: ",
      dismissedByHub: "Zamítnuto Hubem: ",
      text: "Text pravidla",
      whySummary: "Proč",
      save: "Uložit",
      cancel: "Zrušit",
      edit: "Upravit",
      activate: "Aktivovat",
      backToDraft: "Zpět do návrhů",
      agents: {
        vratislav: {
          title: "Learnings Vratislava",
          intro:
            "Pravidla z míst, kde Vratislavův verdikt neobstál: Radim jako arbitr ponechal plán nebo kritéria druhé strany, Vratislav stáhl vrácení revize, které lidé rozporovali, nebo po své eskalaci mergnul bez nové práce. Důvod, který se na projektu objeví 3× za týden, se stane návrhem. Do jeho revizí merge requestů, plánů a kritérií jdou jen aktivní pravidla.",
          addHint: "Něco, na co má Vratislav při revizi myslet. Je hned aktivní.",
        },
        prokop: {
          title: "Pravidla Prokopa",
          intro:
            "Pravidla pro to, jak Prokop testuje, z míst, kde jeho výsledek zůstal nejasný, tiket nebyl ve správném stavu, prohlásil ho za netestovatelný, nebo vývojář neúspěšný test rozporoval a tiket pak prošel. Důvod, který se na projektu objeví 3× za týden, se stane návrhem. Do jeho testů a kritérií, která píše, jdou jen aktivní pravidla. Chybějící schopnosti zůstávají v záložce Learnings.",
          addHint: "Něco, na co má Prokop při testování myslet. Je hned aktivní.",
        },
      },
    },
    reviewerLearnings: {
      loadFailed: "Poučení se nepodařilo načíst.",
      saveFailed: "Uložení se nepodařilo.",
      sendFailed: "Odeslání se nepodařilo.",
      lookBackStarted: "Zpětný pohled na dokončené review začal, zkuste to za pár minut.",
      aiNotConfigured: "AI není nastavená.",
      lookBackFailed: "Zpětný pohled se nepodařilo spustit.",
      title: "Poučení Radima",
      intro:
        "Po každém dokončeném review Hubert přečte schůzku a navrhne, co by měl Radim dělat jinak. Pravidlo se k Radimovi dostane až když je aktivní, od jeho další schůzky (i mluvené), a po vzetí zpět zase zmizí. Chyba je něco v nástrojích Hubu: pošlete ji Metoději jako tiket v Hubu.",
      lookBackNow: "Podívat se zpět teď",
      tabs: {
        draft: "Návrhy",
        active: "Aktivní",
        retire_proposed: "Ke zrušení",
        expired: "Vypršelá",
        dismissed: "Zamítnutá",
        all: "Všechna",
      },
      empty: "Nic tu není.",
      kind: { bug: "Chyba", asr: "Slovo", rule: "Pravidlo" },
      everyProject: "Každý projekt",
      seen: "viděno {{count}}×",
      fromReview: "z review ({{mode}}) projektu {{project}}, {{date}}",
      fromReviewAll: "z review ({{mode}}) napříč projekty, {{date}}",
      sentAs: "odesláno jako #{{id}}",
      activatedBy: "aktivoval(a) {{name}}",
      dismissedBy: "zamítl(a) {{name}}",
      why: "Proč: ",
      dismissedByHub: "Zamítnuto Hubem: ",
      text: "Text poučení",
      whySummary: "Proč",
      save: "Uložit",
      cancel: "Zrušit",
      edit: "Upravit",
      sendToMetodej: "Poslat Metoději",
      activate: "Aktivovat",
      backToDraft: "Zpět do návrhů",
    },
    testerLearnings: {
      loadFailed: "Poučení se nepodařilo načíst.",
      title: "Co Prokop zatím neumí otestovat",
      intro:
        "Každá karta je schopnost, kterou Prokop potřeboval, když tiket zůstal neověřený, spolu s tikety, které zablokovala. Pošlete ji Metoději a stane se z ní funkce k vytvoření; zamítnutá se už Prokop navrhovat nebude.",
      tabs: { draft: "Navržená", dismissed: "Zamítnutá" },
      empty: "Nic tu není.",
      blocked_one: "zablokoval {{count}} tiket",
      blocked_few: "zablokoval {{count}} tikety",
      blocked_other: "zablokoval {{count}} tiketů",
      sentBadge: "odesláno Metoději",
      tickets: "Tikety",
      why: "Proč: ",
      dismissedByHub: "Zamítnuto Hubem: ",
      feature: "Funkce",
      projectFor: "Projekt pro funkci",
      pickProject: "Vyberte projekt",
      sendFailed: "Odeslání Metoději se nepodařilo.",
      saveFailed: "Uložení se nepodařilo.",
      send: "Poslat Metoději",
      backToProposed: "Zpět mezi navržená",
    },
    wikiKeeper: {
      ticketFor: "Tiket pro Metoděje:",
      companyWiki: "Firemní wiki:",
      skipped: "Vynecháno: {{title}} ({{reason}})",
      seedMissing: "Seed v repu nenalezen.",
      seed: "Seed: {{checked}} účtů, {{present}} už ve Vaultu",
      seedFiled: ", doplněno: {{list}}",
      seedNoPassword: ", bez čitelného hesla: {{list}}",
      loadFailed: "Nepodařilo se načíst běhy.",
      auditStarted: "Audit spuštěn.",
      watchStarted: "Kontrola změn spuštěna.",
      startFailed: "Nepodařilo se spustit.",
      checkNow: "Zkontrolovat změny teď",
      runAudit: "Spustit audit wiki",
      loading: "Načítám…",
      none: "Kosmas zatím neproběhl.",
      typeAudit: "Audit",
      typeChanges: "Změny",
      quiet_one: "{{count}} projekt bez změn",
      quiet_few: "{{count}} projekty bez změn",
      quiet_other: "{{count}} projektů bez změn",
      wikiOk: "Wiki sedí.",
    },
    whatsapp: {
      pairFailed: "Spárování s WhatsAppem se nepodařilo spustit.",
      unlinkFailed: "WhatsApp se nepodařilo odpojit.",
      historyAsking: "Ptám se telefonu na historii…",
      historyAskingDesc_one:
        "{{count}} spárovaný chat. Výsledek se objeví nad seznamem — může přijít prázdný.",
      historyAskingDesc_few:
        "{{count}} spárované chaty. Výsledek se objeví nad seznamem — může přijít prázdný.",
      historyAskingDesc_other:
        "{{count}} spárovaných chatů. Výsledek se objeví nad seznamem — může přijít prázdný.",
      historyFailed: "Načtení historie se nepodařilo spustit.",
      dateChanged: "Datum změněno.",
      dateChangedDesc:
        "Chybějící zprávy dotáhnete tlačítkem Načíst celou historii (nebo novým propojením).",
      dateFailed: "Datum se nepodařilo změnit.",
      intro:
        "Sledované chaty se čtou průběžně; otevřením uvidíte jejich přepis a co z něj vzniklo.",
      pairNew: "Spárovat nový chat",
      pairTitle: "Spárovat chat",
      pairHint:
        "Skupiny, ve kterých je propojený účet, a soukromé chaty, kde má nějakou zprávu (nejnověji aktivní první). Spárování chat začne sledovat. Chat bez projektu se směruje po zprávách: Hubert vybere projekty, kterých se týká.",
      nothingToPair: "Žádné další nedávné chaty ke spárování.",
      pair: "Spárovat",
      loadHistory: "Načíst celou historii",
      pulling: "Stahuji historii z telefonu pro chaty: {{count}}…",
      pulled: "Poslední stažení historie přineslo zpráv: {{count}}.",
      pulledNothing:
        "Poslední stažení historie nepřineslo nic — telefon odpověděl bez starších zpráv. Spolehlivý způsob, jak chat naplnit, je <b>Účet → Odpojit</b> a propojit znovu: při párování telefon pošle celou historii.",
      notConnected: "WhatsApp není připojený. Propojíte ho pod <b>Účet</b> (vpravo nahoře).",
      pairedEmpty:
        "Zatím žádné spárované chaty. Sledování chatu zahájíte tlačítkem <b>Spárovat nový chat</b>.",
      processed: "zpracováno {{done}}/{{total}}",
      projectFor: "Projekt pro {{name}}",
      hubertPicks: "— vybere Hubert —",
      unpair: "Zrušit spárování",
      change: "změnit",
      suggestionsFrom: "Návrhy od — {{name}}",
      relabelHint:
        "Pouze přeznačí chat: uložené zprávy zůstanou, ať zvolíte cokoli. Nic se nerozpáruje.",
      save: "Uložit",
      account: "Účet",
      accountTitle: "Účet WhatsApp",
      loading: "Načítání…",
      off: "WhatsApp není na serveru zapnutý. Nastavte <c>WHATSAPP_ENABLED=true</c>.",
      linkedAs: "Propojeno jako <b>{{name}}</b>",
      relinkHint:
        "Nové propojení způsobí, že telefon znovu pošle celou historii — tak naplníte chaty spárované, když už byl účet propojený.",
      unlink: "Odpojit",
      scan: "Otevřete WhatsApp → <b>Nastavení → Propojená zařízení → Propojit zařízení</b> a naskenujte:",
      qrAlt: "QR kód pro spárování WhatsAppu",
      connecting: "Připojuji k WhatsAppu…",
      linkPrompt: "Propojte účet WhatsApp, aby se daly sledovat chaty.",
      waitingScan: "Čekám na naskenování…",
      starting: "Spouštím…",
      link: "Propojit WhatsApp",
    },
  },
};
